/**
 * useScrollRestore - 路由返回时恢复滚动位置
 *
 * 解决: 从二级页面返回一级页面时，一级页面回到顶部的问题
 * 原理: 页面隐藏时保存滚动位置到 sessionStorage，页面显示时恢复
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'scroll_positions';

function getScrollContainer(): HTMLElement {
  // AppLayout 使用 <main> 作为独立滚动容器 (overflow-y: auto)
  // 优先查找 <main>，fallback 到 documentElement（非 AppLayout 页面）
  return document.querySelector('main') || document.documentElement;
}

function savePosition(key: string, position: number): void {
  const data = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  data[key] = position;
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

function getPosition(key: string): number | null {
  const data = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  const pos = data[key];
  return typeof pos === 'number' ? pos : null;
}

function clearPosition(key: string): void {
  const data = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
  delete data[key];
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

/**
 * 在需要保持滚动位置的一级页面使用
 * @param pageKey - 页面唯一标识，如 'illuminate', 'execute'
 */
export function useScrollRestore(pageKey: string): void {
  const location = useLocation();
  const isNavigatingAway = useRef(false);
  const hasRestored = useRef(false);

  useEffect(() => {
    const scrollContainer = getScrollContainer();

    // 页面挂载时：尝试恢复滚动位置
    const restoreScroll = () => {
      // 延迟恢复，等待页面渲染完成
      requestAnimationFrame(() => {
        const savedPosition = getPosition(pageKey);
        if (savedPosition !== null && !hasRestored.current) {
          scrollContainer.scrollTo({
            top: savedPosition,
            behavior: 'instant', // 使用 instant 避免动画干扰
          });
          hasRestored.current = true;
          // 恢复后清除，避免重复恢复
          clearPosition(pageKey);
        }
      });
    };

    // 监听页面可见性变化（处理返回按钮）
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // 页面变为可见（返回时）
        restoreScroll();
      } else {
        // 页面隐藏时保存位置
        if (!isNavigatingAway.current) {
          savePosition(pageKey, scrollContainer.scrollTop);
        }
      }
    };

    // 监听页面即将卸载
    const handleBeforeUnload = () => {
      isNavigatingAway.current = true;
      savePosition(pageKey, scrollContainer.scrollTop);
    };

    // 初始恢复
    restoreScroll();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    // 使用 popstate 监听返回按钮
    const handlePopState = () => {
      // 浏览器返回时也会触发 visibilitychange，这里作为备选
      setTimeout(restoreScroll, 0);
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      // 组件卸载时保存位置（如果是导航到其他页面）
      if (!isNavigatingAway.current) {
        savePosition(pageKey, scrollContainer.scrollTop);
      }
    };
  }, [pageKey, location.pathname]);
}

/**
 * 在离开一级页面前往二级页面时调用，确保保存滚动位置
 */
export function useScrollSaveOnLeave(pageKey: string): () => void {
  return () => {
    const scrollContainer = getScrollContainer();
    savePosition(pageKey, scrollContainer.scrollTop);
  };
}
